import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Phone, Lock, Send, LogIn, Smartphone } from 'lucide-react';

import { setAlert } from '../../actions/alert';
import { login, getOTP } from '../../actions/auth';

const Login = (props) => {
    const [formData, setFormData] = useState({
        phone: '',
        OTP: ''
    });
    const { phone, OTP } = formData;
    const [reSendOTP, setReSendOTP] = useState(false);
    const [isLoadingOTP, setIsLoadingOTP] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    const onChange = e => {
        setFormData({ 
            ...formData,
            [e.target.name]: e.target.value 
        });
    };

    const onSubmit = e => {
        e.preventDefault();
        if (!phone || !OTP) {
            props.setAlert('Please enter both phone number and OTP', 'danger');
            return;
        }
        setIsLoggingIn(true);
        props.login({
            phone,
            OTP
        });
        setTimeout(() => setIsLoggingIn(false), 1000);
    };

    const requestOTP = e => {
        e.preventDefault();
        if (phone.length !== 10) {
            props.setAlert('Please enter a valid 10-digit phone number', 'danger');
            return;
        }
        setIsLoadingOTP(true);
            setReSendOTP(true);
            props.getOTP({ 
            phone: formData.phone
            });
        setTimeout(() => setIsLoadingOTP(false), 1000);
    };

    if (props.isAuthenticated) {
        return <Navigate to="/inventory/v1.2/" />
    }

    return (        
        <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light position-relative overflow-hidden">
            {/* Background decoration */}
            <div className="position-absolute w-100 h-100">
                <div className="position-absolute top-0 start-0 w-100 h-100" 
                     style={{
                         background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.1) 0%, rgba(80, 200, 120, 0.1) 100%)',
                         clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0% 85%)'
                     }}>
                </div>
                <div className="position-absolute bottom-0 end-0 w-75 h-75"
                     style={{
                         background: 'linear-gradient(45deg, rgba(156, 39, 176, 0.05) 0%, rgba(233, 30, 99, 0.05) 100%)',
                         clipPath: 'polygon(15% 0, 100% 0, 100% 100%, 0 100%)'
                     }}>
                </div>
            </div>

            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-12 col-sm-8 col-md-6 col-lg-5 col-xl-4">
                        <div className="card shadow-lg border-0 rounded-4 overflow-hidden">
                            {/* Header */}
                            <div className="card-header bg-primary text-white text-center py-4 border-0">
                                <div className="mb-3">
                                    <div className="d-inline-flex align-items-center justify-content-center bg-white bg-opacity-20 rounded-circle p-3">
                                        <Smartphone size={32} className="text-white" />
                                    </div>
                                </div>
                                <h3 className="mb-1 fw-bold">Welcome Back</h3>
                                <p className="mb-0 opacity-75">Sign in to your account</p>
                            </div>

                            {/* Form */}
                            <div className="card-body p-4">
                                <form onSubmit={onSubmit}>
                                    {/* Phone Number Field */}
                                    <div className="mb-4">
                                        <label className="form-label fw-semibold text-dark mb-2">
                                            <Phone size={16} className="me-2" />
                                            Mobile Number
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-end-0">
                                                <Phone size={18} className="text-muted" />
                                            </span>
                    <input
                                                type="tel"
                                                name="phone"
                        value={phone}
                                                onChange={onChange}
                                                className="form-control form-control-lg border-start-0 ps-0"
                                                placeholder="Enter 10-digit mobile number"
                                                maxLength="10"
                                                required
                                                style={{ fontSize: '16px' }}
                                            />
                                        </div>
                                        <small className="text-muted">We'll send you a verification code</small>
                                    </div>

                                    {/* Request OTP Button */}
                                    <div className="mb-4">
                                        <button 
                                            type="button"
                                            onClick={requestOTP}
                                            disabled={phone.length !== 10 || isLoadingOTP}
                                            className={`btn ${reSendOTP ? 'btn-outline-primary' : 'btn-primary'} w-100 py-2 fw-semibold`}
                                        >
                                            {isLoadingOTP ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    <Send size={18} className="me-2" />
                                                    {reSendOTP ? 'Resend OTP' : 'Request OTP'}
                                                </>
                                            )}
                                        </button>
                </div>

                                    {/* OTP Field */}
                                    <div className="mb-4">
                                        <label className="form-label fw-semibold text-dark mb-2">
                                            <Lock size={16} className="me-2" />
                                            Verification Code
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-end-0">
                                                <Lock size={18} className="text-muted" />
                                            </span>
                    <input
                                                type="text"
                                                name="OTP"
                                                value={OTP}
                                                onChange={onChange}
                                                className="form-control form-control-lg border-start-0 ps-0 text-center fw-bold"
                                                placeholder="Enter 4-digit OTP"
                                                maxLength="4"
                                                minLength="4"
                                                pattern="[0-9]{4}"
                                                required
                                                style={{ fontSize: '18px', letterSpacing: '0.5em' }}
                    />
                </div>
                                        {reSendOTP && (
                                            <small className="text-success">
                                                <i className="fas fa-check-circle me-1"></i>
                                                OTP sent successfully!
                                            </small>
                                        )}
                                    </div>

                                    {/* Login Button */}
                                    <button 
                                        type="submit"
                                        disabled={!phone || !OTP || isLoggingIn}
                                        className="btn btn-primary btn-lg w-100 py-3 fw-bold shadow-sm"
                                        style={{ borderRadius: '12px' }}
                                    >
                                        {isLoggingIn ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Signing In...
                                            </>
                                        ) : (
                                            <>
                                                <LogIn size={20} className="me-2" />
                                                Sign In
                                            </>
                                        )}
                                    </button>
                                </form>

                                {/* Help Text */}
                                <div className="text-center mt-4">
                                    <small className="text-muted">
                                        Having trouble? Contact your administrator
                                    </small>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="card-footer bg-light text-center py-3 border-0">
                                <small className="text-muted">
                                    Secure login powered by SMS verification
                                </small>
                            </div>
                        </div>

                        {/* Additional Info */}
                        <div className="text-center mt-4">
                            <div className="row g-3">
                                <div className="col-4">
                                    <div className="d-flex flex-column align-items-center">
                                        <div className="bg-primary bg-opacity-10 rounded-circle p-2 mb-2">
                                            <Phone size={20} className="text-primary" />
                                        </div>
                                        <small className="text-muted">SMS Based</small>
                                    </div>
                                </div>
                                <div className="col-4">
                                    <div className="d-flex flex-column align-items-center">
                                        <div className="bg-success bg-opacity-10 rounded-circle p-2 mb-2">
                                            <Lock size={20} className="text-success" />
                                        </div>
                                        <small className="text-muted">Secure</small>
                                    </div>
                                </div>
                                <div className="col-4">
                                    <div className="d-flex flex-column align-items-center">
                                        <div className="bg-info bg-opacity-10 rounded-circle p-2 mb-2">
                                            <Send size={20} className="text-info" />
                                        </div>
                                        <small className="text-muted">Fast</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

Login.propTypes = {
    isAuthenticated: PropTypes.bool,
    setAlert: PropTypes.func.isRequired,
    getOTP: PropTypes.func.isRequired,
    login: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
    isAuthenticated: state.auth.isAuthenticated
});

export default connect(mapStateToProps, { setAlert, getOTP, login })(Login);